"use client";

import BFLButton from "@repo/shared-ui/BFLButton";
import { dataLayer } from "@repo/analytics/data-layer-utils";
import {
  dlElementConstants,
  dlEventConstants,
} from "@repo/analytics/data-layer.constants";
import {
  selectIsAuthSuccessOrPerpetual,
  setIsCommonSsoModalVisible,
  store,
  useAppSelector,
} from "@repo/web-store";
import React, { FC, useCallback, useMemo, useState } from "react";
import WatchlistModal from "../../../components/Watchlist/WatchlistModal";
import type {
  TrendingStocksCardData,
  TrendingStocksFieldLabels,
  TrendingStockTradeAction,
  TrendingStocksWatchlistConfig,
} from "../types";
import TredingStockCardAccordion from "./TredingStockCardAccordion";
import TredingStockCardFooter from "./TredingStockCardFooter";
import TredingStockCardBadge from "./TredingStockCardBadge";

interface TrendingStockCardProps {
  stock: TrendingStocksCardData;
  fieldLabels?: TrendingStocksFieldLabels;
  watchlist?: TrendingStocksWatchlistConfig;
  buyText: string;
  sellText: string;
  onTradeClick: (
    action: TrendingStockTradeAction,
    stock: TrendingStocksCardData,
  ) => void | Promise<void>;
}

const TrendingStockCard: FC<TrendingStockCardProps> = ({
  stock,
  fieldLabels,
  watchlist,
  buyText,
  sellText,
  onTradeClick,
}) => {
  const loginStatus = useAppSelector(selectIsAuthSuccessOrPerpetual);
  const [isExpanded, setIsExpanded] = useState(
    Boolean(stock.isExpandedDefault),
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const isBookmarked = false;
  const gainSoFar = String(stock.gainSoFar ?? "");
  const percentageChange = String(stock.percentageChange ?? "");
  const valueChange = String(stock.valueChange ?? "");
  const hasExpandableDetails = Boolean(
    stock.buyPrice || stock.targetPrice || stock.stopLoss || stock.potentialReturns,
  );
  const callOptionLabel = stock.callOptionLabel?.trim();
  const dateLabel = stock.callDateLabel?.trim() || "--";
  const timeLabel = stock.callTimeLabel?.trim() || "--";

  const isNegativeGain = useMemo(() => gainSoFar.includes("-"), [gainSoFar]);
  const isNegativeChange = useMemo(
    () => percentageChange.includes("-") || valueChange.includes("-"),
    [percentageChange, valueChange],
  );

  const trendColorClass = isNegativeChange
    ? "text-[#b40000]"
    : "text-[#067161]";
  const trendArrowClass = isNegativeChange
    ? String.raw`before:content-['\e981']`
    : String.raw`before:content-['\e980']`;

  const selectedStock = useMemo(
    () => ({
      exchange: stock.exchange,
      token: stock.token,
      symbol: stock.symbol,
      fullName: stock.stockName,
    }),
    [stock.exchange, stock.stockName, stock.symbol, stock.token],
  );

  const handleBookmarkClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      const analyticsData = {
        section_title: watchlist?.section_title ?? "Trending stocks",
        component: watchlist?.section_title ?? "Trending stocks",
        event: dlEventConstants.CLICK,
        element: dlElementConstants.WATCH_LIST_ICON_CLICK,
        cta_text: watchlist?.cta_text ?? "Add to Watchlist",
        event_type: dlEventConstants.ICON_CLICK,
      };

      const cleverTapObj = {
        EP_PAGE_NAME: dlElementConstants.TRADING_MALL_CLP,
        EP_TILE_CLICKED: stock.stockName ?? stock.symbol,
        EP_JOURNEY_NAME: dlElementConstants.TRADING_MALL_CLP_EP,
      };
      dataLayer("", analyticsData, cleverTapObj);

      if (!loginStatus) {
        store.dispatch(setIsCommonSsoModalVisible(true));
        return;
      }

      setIsModalOpen(true);
    },
    [
      loginStatus,
      stock.stockName,
      stock.symbol,
      watchlist?.cta_text,
      watchlist?.section_title,
    ],
  );

  const handleBuyClick = useCallback(() => {
    onTradeClick("buy", stock);
  }, [onTradeClick, stock]);

  const handleSellClick = useCallback(() => {
    onTradeClick("sell", stock);
  }, [onTradeClick, stock]);

  return (
    <article
      aria-label={`${stock.symbol} trading call`}
      className="w-full max-w-[700px] rounded-md border border-[#E5E5E5] bg-white p-[12px] md:p-[16px] font-sans text-gray-800"
    >
      <div className="flex gap-3 flex-col pb-0">
        <div className="flex select-none items-center justify-between">
          <div className="text-[12px] md:text-sm font-medium text-[#1A1A1A]">
            {fieldLabels?.gainSoFar ?? "gain so far"}{" "}
            <span
              className={`mx-1 ${isNegativeGain ? "text-[#b40000]" : "text-[#067161]"}`}
            >
              •
            </span>{" "}
            <span
              className={isNegativeGain ? "text-[#b40000]" : "text-[#067161]"}
            >
              {gainSoFar || "--"}
            </span>
          </div>

          {hasExpandableDetails && (
            <BFLButton
              type="button"
              aria-label={isExpanded ? "Collapse details" : "Expand details"}
              className="cursor-pointer p-1 text-[#1A1A1A] transition-colors hover:text-gray-800"
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded((prev) => !prev);
              }}
            >
              <svg
                className={`h-3 w-3 transition-transform duration-300 ${isExpanded ? "rotate-180" : "rotate-0"}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </BFLButton>
          )}
        </div>

        <div className="flex items-start justify-between">
          <div className="flex flex-col gap-2">
            <h2 className="text-[14px] md:text-[16px] font-medium text-[#1A1A1A]">
              {stock.symbol}
            </h2>
            <div className="flex items-center gap-2">
              <TredingStockCardBadge variant="gray">
                {stock.callTypeLabel}
              </TredingStockCardBadge>
              <TredingStockCardBadge variant="teal">
                {stock.callStatusLabel}
              </TredingStockCardBadge>
              {callOptionLabel && (
                <TredingStockCardBadge variant="red">
                  {callOptionLabel}
                </TredingStockCardBadge>
              )}
            </div>
          </div>

          {/* Right Side: Price & Bookmark */}
          <div className="flex items-center gap-4">
            <div className="flex flex-col gap-1 items-end text-right">
              <div
                className={`flex items-center text-[14px] md:text-[16px] font-medium ${trendColorClass}`}
              >
                <span>₹</span>
                <span>{stock.ltp}</span>
                <i className={`icomoon ml-1 w-4 h-4 ${trendArrowClass}`} />
              </div>
              <div className="text-[12px] md:text-[14px] text-gray-500">
                {(valueChange || "--")} ({(percentageChange || "--")})
              </div>
            </div>
            <BFLButton
              className="text-[#4A6784] hover:text-gray-600"
              aria-label={(
                fieldLabels?.addToWatchlistAriaLabel ??
                "Add {symbol} to watchlist"
              ).replace("{symbol}", stock.symbol)}
              onClick={handleBookmarkClick}
            >
              <svg
                className="h-[20px] w-[20px]"
                fill={isBookmarked ? "#4A6784" : "none"}
                stroke="#4A6784"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"
                />
              </svg>
            </BFLButton>
          </div>
        </div>
      </div>

      {hasExpandableDetails && (
        <TredingStockCardAccordion
          isExpanded={isExpanded}
          buyPrice={stock.buyPrice}
          targetPrice={stock.targetPrice}
          stopLoss={stock.stopLoss}
          potentialReturns={stock.potentialReturns}
          moneySymbol="₹"
          labels={fieldLabels}
        />
      )}

      <TredingStockCardFooter
        date={dateLabel}
        time={timeLabel}
        buyText={buyText}
        sellText={sellText}
        onBuyClick={handleBuyClick}
        onSellClick={handleSellClick}
      />

      {watchlist?.modalTitle && (
        <WatchlistModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          selectedStock={selectedStock}
          successMsg={watchlist.successMsg}
          errorMsg={watchlist.errorMsg}
          modalTitle={watchlist.modalTitle}
          modalBtnCta={watchlist.modalBtnCta}
        />
      )}
    </article>
  );
};

export default TrendingStockCard;
